export type TokenUsageFeatureKey =
  | 'table_analysis'
  | 'multi_table_analysis'
  | 'dashboard_insights'
  | 'genai_instructions'
  | 'dashboard_generate'
  | 'autocoder'
  | 'theme_ai'
  | 'theme_ai_screen'
  | 'agent'
  | 'agent_titlebot'
  | 'conversation_index'
  | 'chat'
  // The Tables pane below agent access (#1884): its own key, never 'agent'.
  | 'tables_pane'
  // A guided conversation's chat (28 Oct 2026), billed to the guide's team.
  | 'guided_conversation'
  // X post reads by the import queue's X driver (units are reads, priced per 1K).
  | 'social_x_read'
  // Apify scrapes (units are the runs' real cost in micro-dollars, $1 per 1M).
  | 'social_apify_scrape'
  // YouTube Data API reads (units are quota units, free).
  | 'social_youtube_read'

export interface TokenUsageData {
  table_path?: string
  persona?: string
  analysis_id?: string
  pipeline_id?: string
  batch_size?: number
}

export interface TokenUsageLog {
  id: string
  created_at: number // Unix timestamp (ms)
  user_id: string
  customer: string
  job?: string
  feature_key: TokenUsageFeatureKey
  model: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  cached_tokens: number
  cache_creation_tokens: number
  reasoning_tokens: number
  request_id?: string
  data?: TokenUsageData
  // Server-computed cost in summary.currency (USD). Transient — not persisted.
  cost?: number
}

export interface TokenUsageModelCost {
  model: string
  total_input_tokens: number
  total_output_tokens: number
  total_tokens: number
  total_cached_tokens: number
  total_cache_creation_tokens: number
  total_reasoning_tokens: number
  request_count: number
  cost: number
}

export interface TokenUsageSummary {
  total_input_tokens: number
  total_output_tokens: number
  total_tokens: number
  total_cached_tokens: number
  total_cache_creation_tokens: number
  total_reasoning_tokens: number
  request_count: number
  total_cost?: number
  currency?: string
  cost_by_model?: TokenUsageModelCost[]
}

export interface TokenUsageByDimension {
  dimension: string
  summary: TokenUsageSummary
}

export interface TokenUsageListResult {
  logs: TokenUsageLog[]
  total_count: number
  limit: number
  offset: number
}

export interface TokenUsageQuery {
  customer?: string
  job?: string
  user_id?: string
  feature?: TokenUsageFeatureKey
  model?: string
  start_time?: number // Unix timestamp (ms)
  end_time?: number // Unix timestamp (ms)
  limit?: number
  offset?: number
}

export const FEATURE_LABELS: Record<TokenUsageFeatureKey, string> = {
  table_analysis: 'Table Analysis',
  multi_table_analysis: 'Multi-Table Analysis',
  dashboard_insights: 'Dashboard Insights',
  genai_instructions: 'GenAI Instructions',
  dashboard_generate: 'Dashboard Generate',
  autocoder: 'AutoCoder',
  theme_ai: 'Theme AI',
  theme_ai_screen: 'Theme AI junk check',
  agent: 'Agent',
  agent_titlebot: 'Agent Title',
  conversation_index: 'Conversation Index',
  chat: 'Chat',
  tables_pane: 'Tables pane',
  guided_conversation: 'Guided conversations',
  social_x_read: 'X post reads',
  social_apify_scrape: 'Apify scrapes',
  social_youtube_read: 'YouTube reads',
}

/**
 * The data sources Sky is billed for beside AI (Kai, 3 Nov 2026): the import
 * queue's X, Apify and YouTube reads (goapi/pkg/controller/import_queue_usage.go).
 * The ONE list of them: every page that splits AI from data sources reads it.
 */
export const DATA_SOURCE_FEATURE_KEYS: readonly TokenUsageFeatureKey[] = ['social_x_read', 'social_apify_scrape', 'social_youtube_read']

// Pricing types
export interface ModelPricing {
  model: string
  provider: string
  input_price_per_1k: number
  output_price_per_1k: number
  cache_read_price_per_1k?: number
  currency: string
}

export type ModelPricingMap = Record<string, ModelPricing>
